import { queryOptions } from "@tanstack/react-query";
import type { UktzedEntry, UktzedNode } from "../../../shared/api.ts";
import { formatTariffCode } from "../../../shared/uktzed.ts";
import { api, queryKeys } from "./api.ts";

/** The tariff changes only when an admin imports a new version; the server also answers 304 for an unchanged one. */
const STALE_MS = 30 * 60_000;

export const uktzedQueries = {
  children: (parent: string | null) =>
    queryOptions({ queryKey: queryKeys.uktzed.children(parent), queryFn: () => api.uktzedChildren(parent), staleTime: STALE_MS }),
  node: (id: string) => queryOptions({ queryKey: queryKeys.uktzed.node(id), queryFn: () => api.uktzedNode(id), staleTime: STALE_MS }),
};

/** The nodes of a list of children or of a path, without the grouping lines between them. */
export function nodesOf(entries: UktzedEntry[]): UktzedNode[] {
  return entries.filter((entry): entry is UktzedNode => entry.level !== "group");
}

/** The dictionary page, opened at a node when `id` is given. */
export function dictionaryHref(id?: string | null): string {
  return id ? `/app/uktzed/${encodeURIComponent(id)}` : "/app/uktzed";
}

/** The code shown for a node: the section's Roman numeral, or the code as the tariff prints it. */
export function nodeCode(node: Pick<UktzedNode, "id" | "level">): string {
  return node.level === "section" ? node.id : formatTariffCode(node.id);
}

/**
 * Splits the leading dashes from a tariff title. The printed tariff shows a line's depth with them:
 * `- - інші` is two steps below its heading.
 *
 * @param title - Title as printed.
 * @returns How many dashes, and the words after them.
 */
export function splitDashes(title: string): { dashes: number; text: string } {
  const lead = /^(?:\s*[-–—](?=\s))+\s*/.exec(title);
  if (!lead) return { dashes: 0, text: title.trim() };
  return { dashes: (lead[0].match(/[-–—]/g) ?? []).length, text: title.slice(lead[0].length).trim() };
}

/** A title without its dashes and the colon that introduces sub-items, for places that show the level another way. */
export function plainTitle(title: string): string {
  return splitDashes(title).text.replace(/\s*:\s*$/, "");
}
